import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  getTutorialMode,
  DEFAULT_TUTORIAL_MODE,
  TUTORIAL_MODE_EVENT,
  type TutorialMode,
} from '@/lib/utils/tutorialMode';

/**
 * Reactive binding for the Tutorial Mode setting (see tutorialMode.ts).
 *
 * A single subscription in the provider keeps the value in sync with the
 * localStorage-backed setting via the global change event; consumers read it
 * with `useTutorialMode()` (cheap `useContext`, no per-instance listener).
 * Must wrap every React root that renders tooltips — context does not cross
 * `createRoot` boundaries, so InfluencerChatDrawer's separate root declares its own.
 */
/**
 * Exported so a subtree can override the effective mode locally — e.g. the
 * Tutorial Mode preview dialog wraps its live examples in this provider to
 * demonstrate on/off without mutating the user's real setting.
 */
export const TutorialModeContext = createContext<TutorialMode>(DEFAULT_TUTORIAL_MODE);

export function TutorialModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<TutorialMode>(getTutorialMode);

  useEffect(() => {
    const handler = (e: Event) => {
      setMode((e as CustomEvent<TutorialMode>).detail);
    };
    window.addEventListener(TUTORIAL_MODE_EVENT, handler);
    return () => window.removeEventListener(TUTORIAL_MODE_EVENT, handler);
  }, []);

  return (
    <TutorialModeContext.Provider value={mode}>
      {children}
    </TutorialModeContext.Provider>
  );
}

export function useTutorialMode(): TutorialMode {
  return useContext(TutorialModeContext);
}
